import classNames from 'classnames';
import styles from './ProductGrid.module.scss';
import { overwriteImage } from './ImageOverwrite';
import {
  ADD_TO_BASKET,
  ALTERNATE_IMAGE,
  dispatchCitrusAnalytics,
  dispatchImmediateAnalyticsEvent,
  GLOBAL_INTERACTION,
} from '../analytics/analyticsEvents';
import ProductCard from '@JohnLewisPartnership/jl-design-system/components/product-card/ProductCard';
import ProductCardDetailed from '@JohnLewisPartnership/jl-design-system/components/product-card/ALPHA/ProductCardDetailed';
import CompareCheckbox from '../compare-checkbox/CompareCheckbox';
import NonPromoMessage from '../non-promo-message/NonPromoMessage';
import React from 'react';
import { useCompareContext } from '../../context/CompareContext';
import { useBreadcrumbContext } from '../../context/BreadcrumbContext';
import { mapProduct } from '../analytics/utils/mapProduct';
import { useExperiencesContext } from '../experiences/Experiences';
import { useRouter } from 'next/router';
import { useApplicationSettingsContext } from '../../context/ApplicationSettingsContext';

export const useProductCardContainerData = () => {
  const compare = useCompareContext();
  const { category } = useBreadcrumbContext();
  const { basketBFFService } = useApplicationSettingsContext();
  const { asPath: path } = useRouter();

  const {
    atbBlockList = [],
    imageOverwriteFeature,
    isAltImageOnHoverFlagOn,
    isListViewOn,
    isMobileAltImgsFeatureFlagOn,
    isNowWasFlagOn,
    isPriceReductionFlagOn,
    isPromoQuickViewFlagOn,
    isSponsoredTitleFlagOn,
    isSocialProofingProductCardOn,
  } = useExperiencesContext();

  return {
    compare,
    category,
    basketBFFService,
    path,
    experience: {
      atbBlockList,
      imageOverwriteFeature,
      isAltImageOnHoverFlagOn,
      isListViewOn,
      isMobileAltImgsFeatureFlagOn,
      isNowWasFlagOn,
      isPriceReductionFlagOn,
      isPromoQuickViewFlagOn,
      isSponsoredTitleFlagOn,
      isSocialProofingProductCardOn,
    },
  };
};

const ProductCardContainer = ({
  product,
  index,
  imageSize,
  imageWidths,
  isLargeCardSize,
  isMobileBreakpoint,
  isBelowQuickViewBreakpoint,
  handleDisplayQuickAdd,
  openEnergyDrawer,
  handleAddToBasketClick,
  fetchQuickViewData,
  handleDisplayM2MProductSelector,
  socialProofingMessages,
  pricePrefix,
}) => {
  const { compare, category, basketBFFService, path, experience } =
    useProductCardContainerData();

  const {
    atbBlockList = [],
    imageOverwriteFeature,
    isAltImageOnHoverFlagOn,
    isListViewOn,
    isMobileAltImgsFeatureFlagOn,
    isNowWasFlagOn,
    isPriceReductionFlagOn,
    isPromoQuickViewFlagOn,
    isSponsoredTitleFlagOn,
    isSocialProofingProductCardOn,
  } = experience;

  const {
    adId,
    defaultSkuId,
    defaultVariantId,
    highlightedAttributes,
    id,
    image,
    isSponsored,
    nonPromoMessages,
    price,
    productId,
    promoMessages,
    selectedSwatch,
    title,
    type,
    url,
  } = product;

  const featuresArray = [];

  highlightedAttributes?.forEach((feature) => {
    featuresArray.push(
      `${feature.hideNameOnHighlight ? '' : feature.name?.concat(':') || ' '} ${
        feature.value || '  '
      }`
    );
  });

  const productEventData = mapProduct(product, index);

  const handleCompareClick = () => {
    compare.toggle(id, category);
    if (isSponsored) dispatchCitrusAnalytics(id, adId);
  };

  const hasCompare =
    product.compare && product.type === 'product' && compare.isAvailable;
  const comparing = compare.products.includes(id || productId);
  const compareProps = hasCompare
    ? {
        checked: comparing,
        disabled: compare.isDisabled && !comparing,
        onChange: handleCompareClick,
      }
    : {};

  const swatchToUse = selectedSwatch?.isColorOfDefaultVariant
    ? undefined
    : selectedSwatch;

  const handleM2MClick = (e) => {
    e.preventDefault();
    const clickedElement = e.currentTarget;
    const targetHref = clickedElement
      ? clickedElement.getAttribute('href')
      : '';
    handleDisplayM2MProductSelector(targetHref);
  };

  const getClickHandlerByType = (event) => {
    switch (type) {
      case 'm2mFabric':
        return handleM2MClick(event);
      default:
        return null;
    }
  };

  const handleClick = (event) => getClickHandlerByType(event);

  const singleSkuAtbAnalytics = () => {
    dispatchImmediateAnalyticsEvent(ADD_TO_BASKET, {
      products: productEventData,
    });

    if (adId) {
      dispatchCitrusAnalytics(id, adId);
    }
  };

  const handleDisplayPromoModal = () => {
    dispatchImmediateAnalyticsEvent(GLOBAL_INTERACTION, {
      type: 'plp',
      subType: 'promotions modal',
      additionalInfo: {},
      properties: {
        promotions: promoMessages,
      },
      products: [
        {
          brand: product.brand,
          sku: defaultSkuId,
          price: price,
          productName: title,
          productId: id,
        },
      ],
    });
  };

  // eslint-disable-next-line no-unused-vars
  const handleAltImageChangeAnalytics = (prodId) => (defaultSkuId) => {
    dispatchImmediateAnalyticsEvent(ALTERNATE_IMAGE, {
      productId: prodId,
    });
  };

  return (
    <div
      data-test="component-grid-column"
      className={classNames(styles['product-grid__product'], {
        [styles['product-grid__product--list-view']]: isListViewOn,
        [styles['product-grid__product--large']]: isLargeCardSize,
      })}
      data-grid-checked={isLargeCardSize ? 'max' : 'min'}
    >
      {isListViewOn ? (
        <ProductCardDetailed
          product={{
            ...product,
            additionalMessages: nonPromoMessages,
            colorSwatches:
              (product.type !== 'productSet' && product.colorSwatches) || null,
            image: overwriteImage({
              imageOverwriteFeature,
              path,
              defaultSkuId: product.defaultSkuId,
              image: image,
            }),
            promoMessages,
            url: type === 'productSet' && url ? `/browse${url}` : null,
            skuId: defaultSkuId,
            variantId: defaultVariantId,
            // features: featuresArray,
          }}
          // className={classNames(styles['product__list-view'])}
          handleQuickAddClick={handleDisplayQuickAdd}
          handleQuickViewClick={fetchQuickViewData}
          // showAdditionalDetails={isListViewAdditionalDetails}
          buttonSettings={{
            hasAddToBasketButton: !atbBlockList.includes(product.defaultSkuId),
            hasEmailMeWhenAvailableButton: true,
            hasMoreInformationButton: true,
            hasNotifyMeButton: true,
          }}
          hasCompare={hasCompare}
          compareProps={compareProps}
          handleEnergyRatingClick={openEnergyDrawer}
          hasEnergySideDrawer
          imageSettings={{
            loading: index < 4 ? 'eager' : 'lazy',
          }}
          pricePrefix={pricePrefix}
          selectedSwatch={swatchToUse}
          sponsoredTitleEnabled={isSponsoredTitleFlagOn && isSponsored}
          atbProps={{
            clientId: 'standard-plp',
            path: basketBFFService,
            onBeforeAdd: () => handleAddToBasketClick(productEventData),
            onSuccess: productEventData.isMultiSku
              ? undefined
              : singleSkuAtbAnalytics,
          }}
          showPriceReduction={isPriceReductionFlagOn}
          hasPromoMessageModal
          handleDisplayPromoModal={handleDisplayPromoModal}
          isPromoQuickViewFlagOn={isPromoQuickViewFlagOn}
          handleSwatchClick={() =>
            isSponsored && dispatchCitrusAnalytics(id, adId)
          }
          hasNowWasPricing={isNowWasFlagOn}
        />
      ) : (
        <ProductCard
          product={{
            ...product,
            colorSwatches:
              (product.type !== 'productSet' && product.colorSwatches) || null,
            image: overwriteImage({
              imageOverwriteFeature,
              path,
              defaultSkuId: product.defaultSkuId,
              image: product.image,
            }),
            price,
            promoMessages,
            skuId: defaultSkuId,
            url: type === 'productSet' && url ? `/browse${url}` : null,
            variantId: defaultVariantId,
            energy: product.energy,
          }}
          hasShortCtaText
          className={classNames(styles.product)}
          handleQuickAddClick={handleDisplayQuickAdd}
          buttonSettings={{
            hasAddToBasketButton: !atbBlockList.includes(product.defaultSkuId),
            hasEmailMeWhenAvailableButton: true,
            hasMoreInformationButton: true,
            hasNotifyMeButton: true,
            hasQuickViewButton: false,
          }}
          handleEnergyRatingClick={openEnergyDrawer}
          hasEnergySideDrawer
          handleQuickViewClick={fetchQuickViewData}
          handleClick={handleClick}
          handleImageChange={handleAltImageChangeAnalytics(id)}
          hasBrandedTitle
          imageSettings={{
            loading: index < 4 ? 'eager' : 'lazy',
            sizes: imageSize,
            srcSetWidths: imageWidths,
          }}
          hasImageCarousel={isMobileAltImgsFeatureFlagOn}
          hasAltImageOnHover={!isMobileBreakpoint && isAltImageOnHoverFlagOn}
          isProductBadgingOn={true}
          isQuickViewIcon={!isBelowQuickViewBreakpoint}
          isPromoQuickViewFlagOn={isPromoQuickViewFlagOn}
          pricePrefix={pricePrefix}
          selectedSwatch={swatchToUse}
          sponsoredTitleEnabled={isSponsoredTitleFlagOn && isSponsored}
          socialProofMessage={
            isSocialProofingProductCardOn && socialProofingMessages[id]?.title
              ? socialProofingMessages[id]?.title
              : null
          }
          atbProps={{
            clientId: 'standard-plp',
            onSuccess: productEventData.isMultiSku
              ? undefined
              : singleSkuAtbAnalytics,
            path: basketBFFService,
            onBeforeAdd: () => handleAddToBasketClick(productEventData),
          }}
          showPriceReduction={isPriceReductionFlagOn}
          hasPromoMessageModal
          handleDisplayPromoModal={handleDisplayPromoModal}
          handleSwatchClick={() =>
            isSponsored && dispatchCitrusAnalytics(id, adId)
          }
          hasNowWasPricing={isNowWasFlagOn}
        >
          {hasCompare && (
            <CompareCheckbox
              ariaLabel={`Select for product comparison, ${title}`}
              checked={comparing}
              disabled={compare.isDisabled && !comparing}
              id={`checkbox-${product.id}`}
              onCompareClick={handleCompareClick}
            />
          )}
          {nonPromoMessages &&
            nonPromoMessages.map((nonPromoMessage) => (
              <NonPromoMessage
                key={nonPromoMessage}
                message={nonPromoMessage}
              />
            ))}
        </ProductCard>
      )}
    </div>
  );
};

const comparisonFn = (prevProps, nextProps) => {
  return (
    prevProps.product.id === nextProps.product.id &&
    prevProps.socialProofingMessages === nextProps.socialProofingMessages &&
    prevProps.isLargeCardSize === nextProps.isLargeCardSize
  );
};

export default React.memo(ProductCardContainer, comparisonFn);
